import IconArrowUp from "@shared/components/icons/ArrowUp"
import IconRemoveCircle from "@shared/components/icons/RemoveCircle"
import { Dropdown } from "@shared/components/untitledui/base/dropdown/dropdown"
import { cx } from "@shared/components/untitledui/util/cx"
import {
  emptyProseMirrorContent,
  getEditorCardStackState,
} from "@shared/lib/card-stack/prose-mirror-utils"
import type { AiMessageDelta } from "@shared/models/ai"
import type { CardAttachment, LessonStandard, UnifiedCardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import { objectId } from "@shared/util/object-id"
import type { Editor } from "@tiptap/core"
import {
  AlignLeft,
  BookOpen01,
  CheckCircle,
  ClipboardCheck,
  Feather,
  Globe01,
  HelpCircle,
  Lightbulb02,
  Paperclip,
  Rocket02,
  Stars01,
  Users01,
} from "@untitledui/icons"
import { handleError, notifySentry } from "cc-frontend/error-handling"
import prepareLessonAiOpen from "cc-frontend/lib/actions/lesson/LESSON_AI_OPEN"
import prepareLessonAiRespond from "cc-frontend/lib/actions/lesson/LESSON_AI_RESPOND"
import prepareLessonAiSelectSuggestion from "cc-frontend/lib/actions/lesson/LESSON_AI_SELECT_SUGGESTION"
import prepareLessonAiSend from "cc-frontend/lib/actions/lesson/LESSON_AI_SEND"
import type AiChats from "cc-frontend/services/ai-chats"
import type Dialog from "cc-frontend/services/dialog"
import type Filestack from "cc-frontend/services/filestack"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import { createFocusTrap, type FocusTrap } from "focus-trap"
import { isEqual } from "lodash-es"
import { AnimatePresence, motion } from "motion/react"
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react"
import { useDispatch } from "react-app/hooks/useDispatch"
import { useOpenStandardsModal } from "react-app/hooks/useOpenStandardsModal"
import { useEmberService } from "react-app/providers/ember-service-context"
import { Button as AriaButton } from "react-aria-components"
import { AiLessonEdits, markdownToHtml } from "./ai-lesson-edits"
import { AiStreamParser } from "./ai-stream-parser"
import styles from "./LessonAi.module.css"
import LessonAiSuggestions, { type AiSuggestion } from "./LessonAiSuggestions"
import { useCourseStandards } from "./use-course-standards"

/**
 * How long to wait from when we send a message until we receive the first message delta.
 * This may need to increase if we enable thinking.
 */
const START_TIMEOUT = 30_000

/** How long to wait between message deltas before timing out. */
const DELTA_TIMEOUT = 15_000

type AiState =
  // User's turn to send a message.
  | "USER_ASK"
  // Same but it's the first message.
  | "USER_ASK_FIRST"
  // User needs to accept or reject edits.
  | "USER_ACCEPT_REJECT"
  // Assistant is responding (either thinking or streaming).
  | "ASSISTANT_RESPONDING"
  // The user has hit a rate limit. Currently, this can only happen when joining
  // a chat, so we should learn about it before the user types anything.
  | "RATE_LIMITED"
  // A non-retryable error occurred while the assistant was responding.
  | "ERROR_PERMANENT"
  // A retryable error occurred while the assistant was responding.
  | "ERROR_RETRYABLE"

/**
 * A message in the conversation history, in the form used for displaying here
 * (instead of an AiMessage). Includes in-progress assistant messages.
 */
type HistoryMessage = HistoryMessageUser | HistoryMessageAssistant

interface HistoryMessageUser {
  id: string
  role: "user"
  indexInChat: number
  text: string
  files: CardAttachment[]
  standards: LessonStandard[]
}

interface HistoryMessageAssistant {
  id: string
  role: "assistant"
  html: string | null
}

interface Props {
  lesson: UnifiedCardStack & { attributes: { usedAs: "lesson" } }
  course: Course
  planbook: Planbook
  modelsNeededForFirstEdit: ModelsNeededForFirstEdit
  dateString: string
  editorRef: RefObject<Editor | null>
  /** Closes the panel. Reached through {@link Props.requestCloseRef} when the header's X is used. */
  onClose: () => void
  /**
   * Filled in with this panel's exit handler, which confirms first when the assistant has
   * edits waiting. The header's close button lives outside this component but must go through
   * it, the same way legacy portals its close button into the header.
   */
  requestCloseRef?: { current: (() => void) | null }
}

const SUGGESTIONS: Array<AiSuggestion> = [
  {
    id: "DIFFERENTIATE",
    title: "Differentiate",
    subtitle: "Reach every learner",
    icon: Users01,
    colorVar: "--course-color-yellow",
    pattern: "jigsaw.svg",
    prompt:
      "Help me adjust this lesson to support all kinds of learners — including students with IEPs, English learners, and kids who need more challenge or more support.",
  },
  {
    id: "IMPROVE",
    title: "Improve",
    subtitle: "Sharper and better aligned",
    icon: Stars01,
    colorVar: "--course-color-green",
    pattern: "fourPointStars.svg",
    prompt:
      "Take a look at this lesson and help me make it clearer, stronger, and better aligned to the standards.",
  },
  {
    id: "IDEAS",
    title: "Give me Ideas",
    subtitle: "Fresh ways to teach it",
    icon: Lightbulb02,
    colorVar: "--course-color-turquoise",
    pattern: "linesInMotion.svg",
    prompt:
      "I'm looking for fresh ideas to teach the main concepts in this lesson. What creative or effective approaches could I try?",
  },
  {
    id: "QUESTIONS",
    title: "Add questions",
    subtitle: "Quiz-ready in seconds",
    icon: HelpCircle,
    colorVar: "--course-color-cyan",
    pattern: "topography.svg",
    prompt:
      "Add some thoughtful questions I can use to check for understanding — a mix of comprehension and higher-order thinking would be great.",
  },
  {
    id: "HOMEWORK",
    title: "Add homework",
    subtitle: "Practice that sticks",
    icon: BookOpen01,
    colorVar: "--course-color-blue",
    pattern: "graphPaper.svg",
    prompt:
      "Suggest a few meaningful homework options that reinforce what students learned, without overwhelming them.",
  },
  {
    id: "ENGAGE",
    title: "Engage",
    subtitle: "Make it fun and hands-on",
    icon: Rocket02,
    colorVar: "--course-color-purple",
    pattern: "wiggle.svg",
    prompt:
      "Help me make this lesson more fun and interactive — maybe with a game, a hands-on activity, or some student choice.",
  },
  {
    id: "GRAMMAR",
    title: "Fix grammar",
    subtitle: "Clean, correct, polished",
    icon: CheckCircle,
    colorVar: "--course-color-pink",
    pattern: "ticTacToe.svg",
    prompt:
      "Check this lesson for grammar, punctuation, and spelling mistakes, and fix them without changing the meaning.",
  },
  {
    id: "REFORMAT",
    title: "Reformat",
    subtitle: "Easier to read and follow",
    icon: AlignLeft,
    colorVar: "--course-color-coral",
    pattern: "diagonalLines.svg",
    prompt:
      "Clean up the formatting so it’s easier to read and follow — something that’s clear for both teachers and students.",
  },
  {
    id: "SIMPLIFY",
    title: "Simplify",
    subtitle: "Plain words, short sentences",
    icon: Feather,
    colorVar: "--course-color-yellow",
    pattern: "polkaDots.svg",
    prompt:
      "Rewrite this using simpler words and shorter sentences, so it’s easier for younger kids or early readers to understand.",
  },
  {
    id: "EXAMPLES_REAL_WORLD",
    title: "Add real-world examples",
    subtitle: "Connect it to their world",
    icon: Globe01,
    colorVar: "--course-color-green",
    pattern: "endlessClouds.svg",
    prompt:
      "Add examples that connect this lesson to real life — things students might see, use, or care about.",
  },
  {
    id: "ASSESSMENT",
    title: "Add Formative Assessments",
    subtitle: "Check for understanding",
    icon: ClipboardCheck,
    colorVar: "--course-color-turquoise",
    pattern: "plus.svg",
    prompt:
      "Suggest both quick check-ins and end-of-lesson assessments to help me see how well students understood the material.",
  },
]

const ACTION_BUTTON_CLASS =
  "flex cursor-pointer items-center gap-2 rounded-[20px] px-4 py-2.5 text-sm font-medium whitespace-nowrap outline-hidden transition duration-200 hover:-translate-y-px"
const ACCEPT_BUTTON_CLASS = "bg-[#dcfce7] text-[#166534] hover:bg-[#bbf7d0]"
const REVISE_BUTTON_CLASS = "bg-[#dbeafe] text-[#1e40af] hover:bg-[#bfdbfe]"
const REJECT_BUTTON_CLASS = "bg-[#fecaca] text-[#991b1b] hover:bg-[#fca5a5]"
/** Action labels are dropped for icons alone when the panel is too narrow to read them. */
const ACTION_LABEL_CLASS = "cc-hidden @[400px]/ai:inline"

/** The lesson's AI panel, between the course title bar and the lesson itself. */
export default function LessonAi({
  lesson,
  course,
  planbook,
  modelsNeededForFirstEdit,
  dateString,
  editorRef,
  onClose,
  requestCloseRef,
}: Props) {
  const aiChats = useEmberService<AiChats>("ai-chats")
  const filestack = useEmberService<Filestack>("filestack")
  const dialog = useEmberService<Dialog>("dialog")
  const lessonEditor = useEmberService<LessonEditor>("lesson-editor")
  const dispatch = useDispatch()
  const openStandardsModal = useOpenStandardsModal(planbook, course)
  const findStandard = useCourseStandards(course)

  const [chatId] = useState(() => objectId())

  // Interaction state
  const [currentState, setCurrentState] = useState<AiState>("USER_ASK_FIRST")
  const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false)
  const [isSuggestionsMenuOpen, setIsSuggestionsMenuOpen] = useState(false)
  const [isStandardsModalOpen, setIsStandardsModalOpen] = useState(false)
  const [isShowingFilePicker, setIsShowingFilePicker] = useState(false)
  const [isInExitDialog, setIsInExitDialog] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  // Also kept in a ref: the callbacks below read it within the same batch that sets it.
  const [hasPendingEdits, setHasPendingEdits] = useState(false)
  const hasPendingEditsRef = useRef(false)
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null)

  // Pending user message state
  const [inputValue, setInputValue] = useState("")
  const [pendingFiles, setPendingFiles] = useState<CardAttachment[]>([])
  const [pendingStandards, setPendingStandards] = useState<LessonStandard[]>([])
  const nextIndexInChatRef = useRef(0)

  // Pending (incomplete) assistant message state
  const parserRef = useRef<AiStreamParser | null>(null)
  // The assistant's edits to the lesson, applied to its editor. Created on the first send.
  const editsRef = useRef<AiLessonEdits | null>(null)
  const [hasAssistantText, setHasAssistantText] = useState(false)
  const nextTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isUnloadingRef = useRef(false)

  const [history, setHistory] = useState<Array<HistoryMessage>>([])

  const showInput = currentState === "USER_ASK" || currentState === "USER_ASK_FIRST"
  const isSendDisabled = !inputValue.trim()

  /*
   * The AI chat service and the focus trap both hold callbacks from the render that installed
   * them, while what those callbacks do depends on state that keeps moving. They call through
   * this instead, which every render refreshes.
   */
  const latest = useRef<{
    onMessageDeltas: (deltas: AiMessageDelta[]) => void
    onMessageCompleted: () => void
    onRateLimited: (message: string) => void
    onError: (error: unknown, isRetryable: boolean) => void
    tryExit: () => void
  }>(null!)

  // ----------
  // ACTIONS
  // ----------

  const doClose = () => {
    // Set this to deactivate the focus trap, so that it won't be triggered while we're animating out.
    setIsClosing(true)
    onClose()
  }

  const recordResponse = (response: "accept" | "reject" | "revise") => {
    dispatch(
      prepareLessonAiRespond({
        response,
        indexInChat: Math.ceil(history.length / 2) - 1,
        chatId,
        cardStackId: lesson.id,
        lessonTitle: lesson.attributes.title || "",
        planbookId: planbook.id,
      }),
    )
  }

  const handleAccept = () => {
    recordResponse("accept")

    editsRef.current?.accept()
    saveEdits()

    doClose()
  }

  /**
   * Write the lesson the assistant left behind. The edits themselves never persisted — every
   * transaction it made asked UnifiedCardStackEditor to skip its save — so what is stored is
   * still the lesson as it was, and this one action is the whole AI change.
   */
  const saveEdits = () => {
    const editor = editorRef.current
    if (!editor) return

    const { title, content } = getEditorCardStackState(editor)
    const originalTitle = lesson.attributes.title ?? ""
    const originalContent = lesson.attributes.proseMirror ?? emptyProseMirrorContent()

    if (title !== originalTitle) {
      lessonEditor.finishEditingTitle(modelsNeededForFirstEdit, lesson.id, title, originalTitle)
    }
    if (!isEqual(content.json, originalContent.json)) {
      lessonEditor.finishEditingUnifiedLesson(
        modelsNeededForFirstEdit,
        lesson.id,
        content,
        originalContent,
        "ai",
      )
    }
  }

  const handleReject = () => {
    recordResponse("reject")
    editsRef.current?.reject()
    doClose()
  }

  const handleRevise = () => {
    recordResponse("revise")
    setCurrentState("USER_ASK")

    // Leave the edits in place, so the editor keeps showing a diff from the original lesson
    // to the revisions.
  }

  const tryExit = () => {
    // The ref, so an exit prompted by the unload guard sees the same fact the guard saw.
    if (!hasPendingEditsRef.current) {
      doClose()
      return
    }

    setIsInExitDialog(true)
    dialog
      .blank("dialogs/confirm-exit-ai", { className: "dialog--create-annotation" })
      .then((editResult: "accept" | "reject") => {
        if (editResult === "accept") handleAccept()
        else handleReject()
      })
      .catch((error: unknown) => {
        if (error instanceof Error) handleError(error)
        // Else user hit Cancel in the dialog.
      })
      .finally(() => {
        // This reactivates the focus trap.
        setIsInExitDialog(false)
      })
  }

  const sendMessage = (
    indexInChat: number,
    text: string,
    files: CardAttachment[],
    standards: LessonStandard[],
  ) => {
    setHistory((history) => [
      // Remove the previous user and previous AI message. We do this for all messages
      // (not just retries) so that the chat history only shows the most recent messages.
      ...history.slice(2),
      { id: objectId(), role: "user", indexInChat, text, files, standards },
      // Also create an entry for the coming assistant text.
      // It won't be rendered until it has nontrivial content.
      { id: objectId(), role: "assistant", html: null },
    ])

    setCurrentState("ASSISTANT_RESPONDING")
    setIsShowingFilePicker(false)
    setInputValue("")
    setPendingFiles([])
    setPendingStandards([])
    nextIndexInChatRef.current = indexInChat + 1

    parserRef.current = new AiStreamParser()
    setHasAssistantText(false)

    // Tell the editor that AI edits are starting, unless it is still holding the previous
    // edits (see handleRevise). Either way the diff goes back into hiding: the assistant is
    // about to rewrite the cards it edited, block by block.
    if (!editsRef.current) {
      editsRef.current = new AiLessonEdits(editorRef.current!, findStandard)
    }
    if (!hasPendingEditsRef.current) editsRef.current.start()
    editsRef.current.showChanges(false)

    aiChats.sendMessage(chatId, {
      indexInChat,
      text,
      files: files.map((file) => ({
        title: file.title,
        url: file.url,
        mimetype: file.mimetype,
      })),
      standards: standards.map((standard) => ({
        id: standard.id,
        code: standard.code,
        statement: standard.statement,
      })),
    })
    nextTimeoutRef.current = setTimeout(
      () => latest.current.onError("Start timeout", true),
      START_TIMEOUT,
    )

    dispatch(
      prepareLessonAiSend({
        indexInChat,
        chatId,
        cardStackId: lesson.id,
        lessonTitle: lesson.attributes.title || "",
        planbookId: planbook.id,
      }),
    )
  }

  const handleSend = () => {
    const text = inputValue.trim()
    if (!text) return
    sendMessage(nextIndexInChatRef.current, text, pendingFiles, pendingStandards)
  }

  const handleTryAgain = () => {
    // Try again. Note that we overwrite the previous response, not keep it.
    const prevUserMessage = history[history.length - 2] as HistoryMessageUser
    sendMessage(
      prevUserMessage.indexInChat,
      prevUserMessage.text,
      prevUserMessage.files,
      prevUserMessage.standards,
    )
  }

  const handleSuggestionClick = (suggestion: AiSuggestion) => {
    setInputValue((value) => (value ? value + "\n" + suggestion.prompt : suggestion.prompt))

    dispatch(
      prepareLessonAiSelectSuggestion({
        suggestionId: suggestion.id,
        indexInChat: Math.floor(history.length / 2),
        chatId,
        cardStackId: lesson.id,
        lessonTitle: lesson.attributes.title || "",
        planbookId: planbook.id,
      }),
    )
  }

  // ----------
  // ATTACHMENTS
  // ----------

  const handleAddStandard = async () => {
    setIsStandardsModalOpen(true)
    try {
      setPendingStandards(await openStandardsModal(pendingStandards))
    } finally {
      setIsStandardsModalOpen(false)
    }
  }

  const handleUploadFile = () => {
    setIsShowingFilePicker(true)

    const picker = filestack.client.picker({
      // Mime types accepted by Gemini. The list is missing from Gemini's docs, but it's
      // probably the same as for Vertex: https://cloud.google.com/vertex-ai/generative-ai/docs/model-reference/inference#blob
      accept: [
        "application/pdf",
        "audio/mpeg",
        "audio/mp3",
        "audio/wav",
        "image/png",
        "image/jpeg",
        "image/webp",
        "text/plain",
        "video/mov",
        "video/mpeg",
        "video/mp4",
        "video/mpg",
        "video/avi",
        "video/wmv",
        "video/mpegps",
        "video/flv",
      ],
      fromSources: ["local_file_system"],
      maxFiles: 20,
      maxSize: 10 * 1024 * 1024,
      onUploadDone: (res) => {
        const files = filestack.asAttachments(res.filesUploaded)
        setPendingFiles((pendingFiles) => [
          ...pendingFiles,
          ...files.map((file) => ({ ...file, id: objectId() })),
        ])
      },
      onFileUploadFailed: (error: unknown) => {
        console.error("File upload failed:", error)
      },
      onClose: () => {
        setIsShowingFilePicker(false)
      },
    })
    picker.open()
  }

  // ----------
  // AI CHAT SERVICE CALLBACKS
  // ----------

  latest.current = {
    onMessageDeltas: (deltas) => {
      if (currentState === "ERROR_PERMANENT" || currentState === "ERROR_RETRYABLE") return

      if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current)
      nextTimeoutRef.current = setTimeout(
        () => latest.current.onError("Delta timeout", true),
        DELTA_TIMEOUT,
      )

      const textDelta = deltas
        .filter((delta) => delta.content && delta.content.type === "text")
        .map((delta) => delta.content!.content)
        .join("")

      const parser = parserRef.current
      const edits = editsRef.current
      if (!parser || !edits) return

      for (const command of parser.applyDelta(textDelta)) {
        switch (command.type) {
          case "chatAppend":
            if (command.text !== "") {
              setHasAssistantText(true)
              const html = markdownToHtml(command.text)
              setHistory((history) => [
                ...history.slice(0, -1),
                { ...(history[history.length - 1] as HistoryMessageAssistant), html },
              ])
            }
            break
          case "setTitle":
            edits.setTitle(command.title)
            break
          case "setCardValue":
            edits.setCardValue(command.id, command.value, command.isDone)
            break
        }

        if (command.type !== "chatAppend") {
          hasPendingEditsRef.current = true
          setHasPendingEdits(true)
        }
      }
    },

    onMessageCompleted: () => {
      if (currentState === "ERROR_PERMANENT" || currentState === "ERROR_RETRYABLE") return

      if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current)

      // Now that nothing is half-written, show what the assistant changed.
      if (hasPendingEditsRef.current) editsRef.current?.showChanges(true)

      setCurrentState(hasPendingEditsRef.current ? "USER_ACCEPT_REJECT" : "USER_ASK")
    },

    onRateLimited: (message) => {
      setRateLimitMessage(message)
      setCurrentState("RATE_LIMITED")
    },

    onError: (error, isRetryable) => {
      // We don't care unless the AI is currently responding.
      if (currentState !== "ASSISTANT_RESPONDING") return
      // Don't show the error text or notify Sentry if the user is just refreshing the page.
      if (isUnloadingRef.current) return

      if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current)

      // Log the error.
      // We can filter these out as we learn which ones are normal (e.g. client network errors).
      const id = objectId()
      console.error("AI chat error", error, id)
      if (error instanceof Error) notifySentry(error, id, false)
      else notifySentry("Ai chat error: " + error, id, false)

      setCurrentState(isRetryable && history.length > 0 ? "ERROR_RETRYABLE" : "ERROR_PERMANENT")

      // Keep the current incomplete assistant state, including edits.
      // The user can accept or reject them by exiting, so show them what there is.
      if (hasPendingEditsRef.current) editsRef.current?.showChanges(true)
    },

    tryExit,
  }

  // ----------
  // SETUP / TEARDOWN
  // ----------

  useEffect(() => {
    if (requestCloseRef) requestCloseRef.current = () => latest.current.tryExit()

    aiChats.create(
      chatId,
      lesson.id,
      { courseId: course.id, planbookId: planbook.id, dateString, isUnified: true },
      (deltas) => latest.current.onMessageDeltas(deltas),
      () => latest.current.onMessageCompleted(),
      (message) => latest.current.onRateLimited(message),
      (error, isRetryable) => latest.current.onError(error, isRetryable),
    )

    // Leaving the page with edits waiting asks the browser to confirm, and puts our own
    // accept/discard prompt behind it. Anything else means the user is just refreshing, which
    // is not an error worth reporting (see onError).
    const guardBeforeUnload = (event: BeforeUnloadEvent) => {
      // The ref, not the rendered state: an edit that arrived in this same batch still counts.
      if (hasPendingEditsRef.current) {
        event.preventDefault()
        latest.current.tryExit()
      } else {
        isUnloadingRef.current = true
      }
    }
    window.addEventListener("beforeunload", guardBeforeUnload)

    dispatch(
      prepareLessonAiOpen({
        chatId,
        cardStackId: lesson.id,
        lessonTitle: lesson.attributes.title || "",
        planbookId: planbook.id,
      }),
    )

    return () => {
      aiChats.close(chatId)
      window.removeEventListener("beforeunload", guardBeforeUnload)
      if (nextTimeoutRef.current) clearTimeout(nextTimeoutRef.current)
      if (requestCloseRef) requestCloseRef.current = null
    }
  }, [])

  // ----------
  // FOCUS TRAP
  // ----------

  const containerRef = useRef<HTMLDivElement | null>(null)
  const focusTrapRef = useRef<FocusTrap | null>(null)
  const isDeactivatingFocusTrapRef = useRef(false)

  const deactivateFocusTrap = useCallback(() => {
    // Don't trigger tryExit when we call deactivate ourselves.
    isDeactivatingFocusTrapRef.current = true
    try {
      focusTrapRef.current?.deactivate()
    } finally {
      isDeactivatingFocusTrapRef.current = false
    }
  }, [])

  useLayoutEffect(() => {
    focusTrapRef.current = createFocusTrap(containerRef.current!, {
      preventScroll: true,
      escapeDeactivates: true,
      clickOutsideDeactivates: true,
      // Fallback in case the tab-able node below is hidden by an ancestor.
      fallbackFocus: () => containerRef.current!,
      onDeactivate: () => {
        if (!isDeactivatingFocusTrapRef.current) {
          // Try to exit the AI chat when the *user* deactivates the focus trap,
          // first prompting for confirmation if needed.
          // The trap will be re-activated afterwards if they decide not to exit.
          latest.current.tryExit()
        }
      },
    })
    return () => deactivateFocusTrap()
  }, [deactivateFocusTrap])

  // We need to not trap focus when a menu or a modal is open, since those are outside of
  // our DOM tree.
  const shouldTrapFocus =
    hasPendingEdits &&
    !(
      isAttachMenuOpen ||
      isSuggestionsMenuOpen ||
      isStandardsModalOpen ||
      isShowingFilePicker ||
      isInExitDialog ||
      isClosing
    )

  useEffect(() => {
    if (shouldTrapFocus) focusTrapRef.current?.activate()
    else deactivateFocusTrap()
  }, [shouldTrapFocus, deactivateFocusTrap])

  // ----------
  // RENDER
  // ----------

  return (
    <div ref={containerRef} data-test="lesson ai" className={styles.container}>
      <AnimatePresence initial={false}>
        {history.map((message) => (
          <motion.div
            key={message.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col pb-3.5"
          >
            {message.role === "user" ? (
              <div className={styles.userBubble}>{message.text}</div>
            ) : (
              message.html && (
                <div
                  className={styles.assistantBubble}
                  dangerouslySetInnerHTML={{ __html: message.html }}
                />
              )
            )}
          </motion.div>
        ))}
      </AnimatePresence>

      {/* We always need a tab-able node in this container for focus-trap. */}
      <div tabIndex={0} className="focus:outline-hidden">
        <AnimatePresence initial={false}>
          {showInput && (
            <motion.div
              key="input"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col"
            >
              <div className="relative flex max-w-full flex-col rounded-xl border border-[#e5e7eb] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] transition-[border-color,box-shadow] duration-200 focus-within:border-[#d1d5db] focus-within:shadow-[0_1px_2px_1px_hsla(54,5%,70%,0.1),0_4px_10px_hsla(54,10%,70%,0.1)]">
                {/* The prompt gets the full width and the buttons sit under it, as every chat
                    app does it. Beside the prompt they made a column the text had to flow
                    around, which reads as a hole punched in the paragraph. */}
                <div className="flex flex-col">
                  <AiInput
                    value={inputValue}
                    setValue={setInputValue}
                    placeholder={hasPendingEdits ? "Ask AI to revise" : "Ask AI anything"}
                    send={handleSend}
                  />

                  <div className="flex shrink-0 items-center justify-between gap-2 px-3 pb-2">
                    <Dropdown.Root isOpen={isAttachMenuOpen} onOpenChange={setIsAttachMenuOpen}>
                      <AriaButton
                        aria-label="Attach"
                        className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-white text-link-blue shadow-sm outline-hidden transition-colors duration-200 hover:text-[#374151]"
                      >
                        <PaperclipIcon className="size-4" />
                      </AriaButton>
                      <Dropdown.Popover placement="top start">
                        <Dropdown.Menu>
                          <Dropdown.Item
                            label="Include Standard"
                            icon={BookOpen01}
                            onAction={() => void handleAddStandard()}
                          />
                          <Dropdown.Item
                            label="Include File"
                            icon={Paperclip}
                            onAction={handleUploadFile}
                          />
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown.Root>

                    <AriaButton
                      aria-label="Send"
                      isDisabled={isSendDisabled}
                      onPress={handleSend}
                      className="flex cursor-pointer items-center justify-center rounded-3xl bg-link-blue p-1.5 text-white shadow-sm outline-hidden transition-colors duration-200 disabled:cursor-not-allowed disabled:bg-white disabled:text-gray-400"
                    >
                      <IconArrowUp className="size-4.5 fill-current" />
                    </AriaButton>
                  </div>
                </div>
              </div>

              {(pendingStandards.length > 0 || pendingFiles.length > 0) && (
                <div className="flex flex-row flex-wrap gap-2 rounded-lg p-3">
                  {pendingStandards.map((standard) => (
                    <div
                      key={standard.id}
                      className="flex items-center gap-0.5 rounded-[18px] border border-[hsl(0,0%,93%)] py-0 pr-1 pl-2 text-xs"
                    >
                      <span className="shrink-0 font-semibold text-[#0369a1]">{standard.code}</span>
                      <span className="max-w-10 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-[#374151]">
                        {standard.statement}
                      </span>
                      <RemoveChipButton
                        label={`Remove ${standard.code ?? "standard"}`}
                        remove={() =>
                          setPendingStandards((standards) =>
                            standards.filter((s) => s.id !== standard.id),
                          )
                        }
                      />
                    </div>
                  ))}

                  {pendingFiles.map((file, fileIndex) => (
                    <div
                      key={file.id}
                      className="flex items-center gap-1 rounded-full border border-[hsl(0,0%,93%)] py-0 pr-0.5 pl-2 text-xs"
                    >
                      <PaperclipIcon className="size-2.5 shrink-0 text-[#6b7280]" />
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noreferrer"
                        className="max-w-20 flex-1 overflow-hidden font-medium text-ellipsis whitespace-nowrap text-[#374151]"
                      >
                        {file.title}
                      </a>
                      <RemoveChipButton
                        label={`Remove ${file.title}`}
                        remove={() =>
                          setPendingFiles((files) =>
                            files.filter((_, index) => index !== fileIndex),
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="rounded-b-xl bg-[hsl(54,5%,98%)] px-3 py-2">
                <LessonAiSuggestions
                  suggestions={SUGGESTIONS}
                  select={handleSuggestionClick}
                  onMenuOpenChange={setIsSuggestionsMenuOpen}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {currentState === "ASSISTANT_RESPONDING" && (
          <div className="animate-pulse py-4 text-center text-base text-[#f59e0b]">
            <ThinkingSpinner />
            {hasAssistantText ? "Working..." : "Thinking..."}
          </div>
        )}

        {currentState === "USER_ACCEPT_REJECT" && (
          <ResponseActions>
            <AriaButton
              onPress={handleAccept}
              className={cx(ACTION_BUTTON_CLASS, ACCEPT_BUTTON_CLASS)}
            >
              <CheckIcon className="size-4" />
              <span className={ACTION_LABEL_CLASS}>Accept</span>
            </AriaButton>
            <AriaButton
              onPress={handleRevise}
              className={cx(ACTION_BUTTON_CLASS, REVISE_BUTTON_CLASS)}
            >
              <RetryIcon className="size-4" />
              <span className={ACTION_LABEL_CLASS}>Revise</span>
            </AriaButton>
            <AriaButton
              onPress={handleReject}
              className={cx(ACTION_BUTTON_CLASS, REJECT_BUTTON_CLASS)}
            >
              <CloseIcon className="size-4" />
              <span className={ACTION_LABEL_CLASS}>Discard</span>
            </AriaButton>
          </ResponseActions>
        )}

        {currentState === "RATE_LIMITED" && (
          <Notice title="You've hit the limit!">
            You are out of AI responses for now. {rateLimitMessage}
          </Notice>
        )}

        {currentState === "ERROR_PERMANENT" && (
          <>
            <Notice title="Something went wrong" />
            <ResponseActions>
              {hasPendingEdits ? (
                <>
                  <AriaButton
                    onPress={handleAccept}
                    className={cx(ACTION_BUTTON_CLASS, ACCEPT_BUTTON_CLASS)}
                  >
                    <CheckIcon className="size-4" />
                    <span className={ACTION_LABEL_CLASS}>Accept</span>
                  </AriaButton>
                  <AriaButton
                    onPress={handleReject}
                    className={cx(ACTION_BUTTON_CLASS, REJECT_BUTTON_CLASS)}
                  >
                    <CloseIcon className="size-4" />
                    <span className={ACTION_LABEL_CLASS}>Discard</span>
                  </AriaButton>
                </>
              ) : (
                <AriaButton
                  onPress={onClose}
                  className={cx(ACTION_BUTTON_CLASS, REVISE_BUTTON_CLASS)}
                >
                  <IconRemoveCircle className="size-4 fill-current" />
                  Close
                </AriaButton>
              )}
            </ResponseActions>
          </>
        )}

        {currentState === "ERROR_RETRYABLE" && (
          <>
            <Notice title="Something went wrong" />
            <ResponseActions>
              {hasPendingEdits ? (
                <>
                  <AriaButton
                    onPress={handleAccept}
                    className={cx(ACTION_BUTTON_CLASS, ACCEPT_BUTTON_CLASS)}
                  >
                    <CheckIcon className="size-4" />
                    <span className={ACTION_LABEL_CLASS}>Accept as-is</span>
                  </AriaButton>
                  <AriaButton
                    onPress={handleTryAgain}
                    className={cx(ACTION_BUTTON_CLASS, REVISE_BUTTON_CLASS)}
                  >
                    <RetryIcon className="size-4" />
                    <span className={ACTION_LABEL_CLASS}>Try Again</span>
                  </AriaButton>
                  <AriaButton
                    onPress={handleReject}
                    className={cx(ACTION_BUTTON_CLASS, REJECT_BUTTON_CLASS)}
                  >
                    <CloseIcon className="size-4" />
                    <span className={ACTION_LABEL_CLASS}>Discard and exit</span>
                  </AriaButton>
                </>
              ) : (
                <AriaButton
                  onPress={handleTryAgain}
                  className={cx(ACTION_BUTTON_CLASS, REVISE_BUTTON_CLASS)}
                >
                  <IconRemoveCircle className="size-4 fill-current" />
                  Try Again
                </AriaButton>
              )}
            </ResponseActions>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * The prompt box. Grows with its content, and sends on Enter — a newline needs Shift.
 */
function AiInput({
  value,
  setValue,
  placeholder,
  send,
}: {
  value: string
  setValue: (value: string) => void
  placeholder: string
  send: () => void
}) {
  return (
    // The textarea and the mirror share one grid cell: the mirror holds the same text at the
    // same metrics, so the cell — and with it the textarea — is always exactly text-tall.
    // Measuring scrollHeight instead loses to the flex column this sits in.
    <div className="grid w-full">
      <textarea
        autoFocus
        rows={1}
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault()
            send()
          }
        }}
        className={cx(
          INPUT_TEXT_CLASS,
          "col-start-1 row-start-1 resize-none overflow-hidden border-none bg-transparent text-gray-900 outline-hidden placeholder:font-normal placeholder:text-gray-400",
        )}
      />
      <span
        aria-hidden="true"
        className={cx(INPUT_TEXT_CLASS, "invisible col-start-1 row-start-1")}
      >
        {/* The trailing space keeps a final newline from collapsing. */}
        {value + " "}
      </span>
    </div>
  )
}

/** Metrics the prompt textarea and its sizing mirror must agree on exactly. */
const INPUT_TEXT_CLASS =
  "px-3 py-2 text-[0.9375rem] leading-normal font-medium break-words whitespace-pre-wrap"

function ResponseActions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap items-center justify-center gap-3 py-4">{children}</div>
}

function Notice({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="m-2.5 flex flex-col items-center rounded-[10px] bg-[rgb(225,225,225)] p-3.5">
      <div className="text-lg font-bold text-[orangered]">{title}</div>
      {children && <div className="text-[0.84375rem]">{children}</div>}
    </div>
  )
}

function RemoveChipButton({ label, remove }: { label: string; remove: () => void }) {
  return (
    <AriaButton
      aria-label={label}
      onPress={remove}
      className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 outline-hidden transition duration-200 hover:bg-[#fef2f2] hover:text-[#dc2626]"
    >
      <CloseIcon className="size-3" />
    </AriaButton>
  )
}

function PaperclipIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  )
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </svg>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

function RetryIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  )
}

/** The bouncing ball shown while the assistant is thinking. */
function ThinkingSpinner() {
  return (
    <svg
      fill="#f59e0b"
      viewBox="0 0 24 24"
      className="ml-1.5 inline-block size-6"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <ellipse cx="12" cy="5" rx="4" ry="4">
        <animate
          id="spinner_jbYs"
          begin="0;spinner_JZdr.end"
          attributeName="cy"
          calcMode="spline"
          dur="0.375s"
          values="5;20"
          keySplines=".33,0,.66,.33"
          fill="freeze"
        />
        <animate
          begin="spinner_jbYs.end"
          attributeName="rx"
          calcMode="spline"
          dur="0.05s"
          values="4;4.8;4"
          keySplines=".33,0,.66,.33;.33,.66,.66,1"
        />
        <animate
          begin="spinner_jbYs.end"
          attributeName="ry"
          calcMode="spline"
          dur="0.05s"
          values="4;3;4"
          keySplines=".33,0,.66,.33;.33,.66,.66,1"
        />
        <animate
          id="spinner_ADF4"
          begin="spinner_jbYs.end"
          attributeName="cy"
          calcMode="spline"
          dur="0.025s"
          values="20;20.5"
          keySplines=".33,0,.66,.33"
        />
        <animate
          id="spinner_JZdr"
          begin="spinner_ADF4.end"
          attributeName="cy"
          calcMode="spline"
          dur="0.4s"
          values="20.5;5"
          keySplines=".33,.66,.66,1"
        />
      </ellipse>
    </svg>
  )
}
